// Implements: F-VER-004, F-VER-006, F-VER-007, F-VER-011, F-VER-014, F-UIX-021, F-UIX-009
/**
 * The `versions` module's client, on top of the single HTTP exit.
 *
 * The tree route answers the nodes and NOTHING about which one is current:
 * that pointer is `current_version_id` of `GET /mockups/{id}`, which the page
 * reads once under the shared `mockupDetailKey` (see `useVersionTree`).
 *
 * The nodes carry no promised order, and this file does not invent one: the
 * order is a presentation decision, made where the tree is drawn.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type {
  VersionDetailView,
  VersionDiffView,
  VersionSummaryView,
  VersionTreeNode,
  VersionTreeView,
} from "../../../types/api";

/** `GET /mockups/{id}/versions`: one keyset page of version summaries. */
interface VersionPageView {
  readonly items: readonly VersionSummaryView[];
  readonly next_cursor?: string | null;
}

export function versionTreePath(mockupId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/versions/tree`;
}

export async function fetchVersionNodes(mockupId: string): Promise<readonly VersionTreeNode[]> {
  const tree = await httpRequest<Partial<VersionTreeView>>(versionTreePath(mockupId));
  return tree?.nodes ?? [];
}

function versionPath(mockupId: string, versionId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/versions/${encodeURIComponent(versionId)}`;
}

function bodyless(what: string, status: number): ApiError {
  return new ApiError(status, UNKNOWN_ERROR_CODE, `${what} answered with no body`, new Headers());
}

/** `GET /mockups/{id}/versions/{version_id}`: one version and a pointer to each of its pieces. */
export async function fetchVersion(
  mockupId: string,
  versionId: string,
): Promise<VersionDetailView> {
  const body = await httpRequest<VersionDetailView>(versionPath(mockupId, versionId));
  if (body === undefined) throw bodyless("GET /mockups/{id}/versions/{version_id}", 200);
  return body;
}

/**
 * Make a version the current one (`POST .../promote`).
 *
 * It only moves the pointer, creates no version and charges nothing, and it
 * lands on the same value however many times it is sent, so the default
 * retry of the client is safe here.
 */
export async function promoteVersion(mockupId: string, versionId: string): Promise<void> {
  await httpRequest<unknown>(`${versionPath(mockupId, versionId)}/promote`, { method: "POST" });
}

// The three downloads moved to `shared/api/versionDownloads` (mockup v3 P4); re-exported unchanged.
export { defaultDownloadName, downloadVersionFile } from "../../../shared/api/versionDownloads";
export type { VersionDownload } from "../../../shared/api/versionDownloads";

/** `GET /mockups/{id}/versions/diff?left&right`: no entry means no piece on either side. */
export async function compareVersions(
  mockupId: string,
  leftVersionId: string,
  rightVersionId: string,
): Promise<VersionDiffView> {
  const query = new URLSearchParams({ left: leftVersionId, right: rightVersionId });
  const body = await httpRequest<Partial<VersionDiffView>>(
    `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/versions/diff?${query.toString()}`,
  );
  return {
    left_version_id: body?.left_version_id ?? leftVersionId,
    right_version_id: body?.right_version_id ?? rightVersionId,
    per_kind: body?.per_kind ?? [],
  };
}

/**
 * Attach, replace or take back the note of a version (`PATCH .../versions/{id}`, F-VER-008).
 * `null` takes it back; the server also reads a blank note as none and bounds it at 140 (P4-B4).
 * It describes an end state, so the client's retry of a 5xx is harmless.
 */
export async function annotateVersion(
  mockupId: string,
  versionId: string,
  note: string | null,
): Promise<VersionSummaryView> {
  const body = await httpRequest<VersionSummaryView>(versionPath(mockupId, versionId), {
    method: "PATCH",
    body: { note },
  });
  if (body === undefined) throw bodyless("PATCH version", 200);
  return body;
}

/** The longest page the version listing serves (`LIMIT_RANGE` of the route). */
const NOTES_PAGE = 200;

/** Never more pages than this: a cursor that never ends must not loop forever. */
const NOTES_MAX_PAGES = 20;

/**
 * Every version's note, from `GET /mockups/{id}/versions` (the tree carries no note, F-VER-014),
 * following the cursor to the last page.
 */
export async function fetchVersionNotes(mockupId: string): Promise<ReadonlyMap<string, string | null>> {
  const notes = new Map<string, string | null>();
  let cursor: string | null = null;
  for (let page = 0; page < NOTES_MAX_PAGES; page += 1) {
    const query = new URLSearchParams({ limit: String(NOTES_PAGE) });
    if (cursor !== null) query.set("cursor", cursor);
    const body = await httpRequest<Partial<VersionPageView>>(
      `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/versions?${query.toString()}`,
    );
    for (const item of body?.items ?? []) notes.set(item.id, item.note);
    cursor = body?.next_cursor ?? null;
    if (cursor === null) break;
  }
  return notes;
}
